import { tr } from "../i18n/LanguageContext";
import React, { useState } from "react";
import { apiErrorMessage } from "../lib/apiError";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MessageDialog = ({ organizer, onClose, initialSubject = "", eventId = null, onSent }) => {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          toUserId: organizer.userId,
          subject: subject.trim(),
          body: body.trim(),
          ...(eventId ? { eventId } : {}),
        }),
      });
      if (res.ok) {
        setSent(true);
        if (onSent) onSent();
      } else {
        const err = await res.json();
        toast({ title: apiErrorMessage(err, tr("Ошибка при отправке")), variant: "destructive" });
      }
    } catch {
      toast({ title: tr("Ошибка при отправке сообщения"), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="eunoia-surface p-6 w-full max-w-lg relative z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="eunoia-title-small flex items-center gap-2">
            <MessageCircle className="h-5 w-5" style={{ color: "var(--eunoia-accent)" }} />
            {tr("Написать организатору")}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-black/5 transition-colors">
            <X className="h-5 w-5" style={{ color: "var(--eunoia-soft)" }} />
          </button>
        </div>

        <p className="eunoia-caption mb-4">
          {tr("Кому:")} <span className="font-medium" style={{ color: "var(--eunoia-deep)" }}>{organizer.name}</span>
        </p>

        {sent ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#dcfce7" }}>
              <Send className="h-6 w-6" style={{ color: "#16a34a" }} />
            </div>
            <p className="eunoia-body font-medium mb-2">{tr("Сообщение отправлено!")}</p>
            <p className="eunoia-caption mb-4">{tr("Организатор получит уведомление на email.")}</p>
            <button onClick={onClose} className="eunoia-btn-secondary">{tr("Закрыть")}</button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--eunoia-deep)" }}>{tr("Тема")}</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="eunoia-input"
                placeholder={tr("Вопрос о событии...")}
                maxLength={200}
                required
                data-testid="message-subject"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--eunoia-deep)" }}>{tr("Сообщение")}</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="eunoia-input min-h-[120px] resize-y"
                placeholder={tr("Здравствуйте! Хочу узнать подробнее...")}
                maxLength={5000}
                required
                data-testid="message-body"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="eunoia-btn-primary w-full flex items-center justify-center gap-2"
              data-testid="send-message-button"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-4 w-4" /> {tr("Отправить")}</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default MessageDialog;
